/* Owlkrown hero animations.
 *
 * Each entry finds its slide by the filename of that slide's own background,
 * never by position. Slides get dragged around in the theme editor, and an
 * index-based lookup silently paints one product's animation onto another's
 * slide: that is exactly what happened on 2026-10-02 when the No Slice slide
 * was moved to the front and inherited the SL3 FlexPlate sequence.
 *
 * Every hero is a PANEL now: media boxed in the left 56% of the slide with a
 * solid ground under the copy on the right, except SteelCore which is the same
 * shape with a video in the box. A full-bleed crossfade is just a panel with
 * split: 100, so there is no second code path for one.
 *
 * Frames that have been RE-UPLOADED carry a ?v= stamp, and the stamp must be
 * bumped every time that frame is replaced again. The opposite was assumed here
 * until 2026-10-11 and it is wrong: an un-stamped cdn.shopify.com URL is served
 * with max-age=31557600, so swapping the file under the same name leaves the
 * edge handing out the OLD image for up to a year. It refreshed for the four
 * sl3 mobile frames within seconds and never refreshed for sl3-desk-3 and -4,
 * so a replacement without a new URL ships a mix of old and new to visitors.
 * A frame that has only ever been uploaded once needs no stamp. The corollary:
 * to REORDER a sequence, renumber the map below; to re-grade one, upload under
 * a new name. Never re-upload different pixels under an existing name.
 *
 * A sequence runs when ITS OWN SLIDE becomes the selected one, never merely
 * when the hero scrolls into view. The hero is a FADE carousel: all six slides
 * sit in the same box and are hidden by opacity alone, so an
 * IntersectionObserver reports every one of them as fully visible the moment
 * the hero appears. Triggering on that ran all three sequences at once on page
 * load, behind slide 1, and every one of them was finished by 8s - long before
 * its own slide came round. Reaching the ProKit slide at 30s showed a still of
 * its LAST frame, the all-colours shot, which is what Indre reported on
 * 2026-10-13. Opacity is invisible to IntersectionObserver; only the
 * is-selected class says which slide a visitor is actually looking at.
 */
(function () {
  var CDN = 'https://cdn.shopify.com/s/files/1/0730/8489/5472/files/';

  /* ---- split slides -------------------------------------------------------
   * Six seconds of the cut test, trimmed from C8375.MP4 at 4-10s with the audio
   * track removed, running in the left 56% of the slide with a solid panel on
   * the right. The copy already sits at x 0.55-0.91, so it lands on the panel.
   *
   * It is a panel and not an overlay on purpose: the footage is handheld and
   * gloved arms sweep through the right of frame, so white copy laid straight
   * over it would flicker between readable and not, frame by frame.
   *
   * Desktop and mobile get their own rendition and only the visible one is ever
   * fetched - the hidden one stays preload="none" and is never told to play. */
  var VID = 'https://cdn.shopify.com/videos/c/vp/ec405b536f3f4493b718161d6b8f146f/ec405b536f3f4493b718161d6b8f146f';
  var SPLITS = [
    {
      id: 'nscut',
      deskKey: 'no-slice-desk-1',
      mobKey: 'no-slice-mob-1',
      desk: VID + '.HD-720p-4.5Mbps-96983938.mp4',
      mob:  VID + '.SD-480p-1.5Mbps-96983938.mp4',
      split: 56,        /* percent of the slide width given to the video */
      focus: '45% 45%'  /* 16:9 source in a narrower panel: this picks what survives */
    }
  ];

  /* ---- panel slides ------------------------------------------------------
   * Same shape as the cut-test slide above, with stills crossfading in the box
   * instead of a video, so this needs the stack and the panel together and
   * gets its own builder.
   *
   * Frame 1 is the base layer and never animates, so the panel is a picture
   * from the first paint rather than black until the first dissolve lands.
   *
   * Frames are cut to the BOX, 1120x1080, not to the slide. A frame composed
   * for a 2000x1080 full-bleed slide has empty space on the right for the type
   * that is simply wasted here, which is what the 2026-10-11 SL3 recut fixed.
   *
   * FIVE frames is the ceiling at the agreed pace. The fifth starts its
   * dissolve at 7.2s and lands at 8.0s, inside the 10s a slide is on screen; a
   * sixth would land at exactly 10.0s and never be seen. A FOUR frame set ends
   * by returning to frame 1 at 7.2s instead, so it keeps moving for the whole
   * 10s rather than holding a dead last frame for four of them.
   *
   * CROP ONLY is the standing rule for these panels. Three attempts at tone
   * work have now been built and withdrawn (two on ProKit, one on M10) and the
   * photographs won every time. Do not start a fourth without being asked. */
  var PANELS = [
    {
      id: 'sl3',
      deskKey: 'sl3-desk-1',
      mobKey: 'sl3-mob-1',
      /* Recut to the panel on 2026-10-11. The old frames were 2000x1080 with the
       * camera in the left half and bare wall on the right for the type to sit
       * on; inside a 1120x1080 box that wall is dead space, so these are fresh
       * 56%-wide crops centred on the camera and the product is about 1.8x
       * bigger on screen. Cropping moved the exposure again (0.171 / 0.162 /
       * 0.123 / 0.107) so all four were rematched to L 0.1381. This is the one
       * hero that IS tone matched, and it was signed off before the rule above.
       *
       * New filenames, so no ?v= needed. The MOBILE frames are the originals:
       * the theme already stacks picture over text on a phone and those squares
       * were composed for it, so only desktop was recut. They keep the stamp
       * from the exposure match earlier the same night. */
      desk: { 1: 'sl3p-desk-1.webp', 2: 'sl3p-desk-2.webp',
              3: 'sl3p-desk-3.webp', 4: 'sl3p-desk-4.webp' },
      mob:  { 1: 'sl3-mob-1.webp',  2: 'sl3-mob-2.webp?v=1791768352',
              3: 'sl3-mob-3.webp?v=1791768352', 4: 'sl3-mob-4.webp?v=1791768352' },
      split: 56,
      fade: 0.8,
      /* unchanged pace: 1.2s still, 0.8s dissolve, back to frame 1 at 7.2s */
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 1, d: 7.2 }
      ]
    },
    {
      id: 'fuji',
      /* The silver and the black grip are the same shot in two finishes. Side
       * by side they morph, which reads as a glitch, so they sit at opposite
       * ends of the run: 1 -> 2 -> 4 -> 5 -> 3.
       *
       * Ran quicker than the other heroes (1.0s + 0.75s) until 2026-10-12,
       * because the US market used to rotate this slideshow at 8s a slide and
       * five frames had to finish inside it. The US override went to 10s with
       * the SL3 work, so there is nothing left to be quick for and it matches
       * SL3 and ProKit: 1.2s still, 0.8s dissolve, last frame lands at 7.2s. */
      deskKey: 'Fuji_FlexGrip_silver_plate_black_handle_Desk',
      mobKey: 'Fuji_FlexGrip_silver_plate_black_handle_Mobile',
      desk: { 1: 'fuji-desk-1.webp', 2: 'fuji-desk-2.webp', 3: 'fuji-desk-3.webp',
              4: 'fuji-desk-4.webp', 5: 'fuji-desk-5.webp' },
      mob:  { 1: 'fuji-mob-1.webp',  2: 'fuji-mob-2.webp',  3: 'fuji-mob-3.webp',
              4: 'fuji-mob-4.webp',  5: 'fuji-mob-5.webp' },
      split: 56,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 4, d: 3.2 }, { f: 5, d: 5.2 }, { f: 3, d: 7.2 }
      ]
    },
    {
      id: 'prokit',
      deskKey: 'prokit-q-hero-desktop',
      mobKey: 'prokit-q-hero-mobile',
      /* Added 2026-10-11, order chosen by Indre: tin, black, patina, OxBlood,
       * then the whole range to close.
       *
       * CROPPED AND NOTHING ELSE. The shoot is high key and the five sit
       * across a 1.30x range (0.393 / 0.355 / 0.308 / 0.402 / 0.392), so this
       * slide is brighter than the others and drifts slightly as it runs.
       *
       * Two graded versions were tried and both rejected: a plain gamma
       * darkened the whole picture, and because the parts are black on a black
       * camera it ate the edge highlights that separate them - 40% of the
       * detail inside the product. A luminance masked version that darkened
       * only the concrete kept the product intact but made the background look
       * artificial. */
      desk: { 1: 'pk-desk-1.webp', 2: 'pk-desk-2.webp', 3: 'pk-desk-3.webp',
              4: 'pk-desk-4.webp', 5: 'pk-desk-5.webp' },
      mob:  { 1: 'pk-mob-1.webp',  2: 'pk-mob-2.webp',  3: 'pk-mob-3.webp',
              4: 'pk-mob-4.webp',  5: 'pk-mob-5.webp' },
      split: 56,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 5, d: 7.2 }
      ]
    },
    {
      id: 'm10',
      deskKey: 'flexgrip-m10-desktop',
      mobKey: 'flexgrip-m10-mobile',
      /* Added 2026-10-13. Indre sent six photographs and five are used: at the
       * agreed 1.2s + 0.8s a sixth frame starts its dissolve at 9.2s and lands
       * at 10.0s, which is the instant the slide is replaced, so nobody would
       * ever see it. The one left out is the plain floating base view; frame 4
       * is the same angle with the Arca clamp fitted, so it says more.
       *
       * OPENS on the base with the door off, battery and card out - Indre's
       * call, and the right one: it is the only picture that shows what the
       * slide actually claims, a door that opens with the plate still on, and
       * it is where the AirTag module sits. Then the camera whole, the grip's
       * shape, the clamp underneath, on the tripod.
       *
       * CROPPED AND NOTHING ELSE - Indre's originals. A darkened set was built
       * and withdrawn the same day. Worth knowing why, because the complaint
       * that started it is real and will come back: the grip is black anodised
       * but the shoot is high key, and it photographs at grey 143 against a
       * wall at 150, so the product is the same tone as its background and
       * reads grey rather than black.
       *
       * What is NOT a grading problem, and must not be treated as one: the
       * wall lighting is uneven left to right in the source photographs, by 18
       * to 55 levels. The tripod frame is the worst - wall 90 on the left
       * against 145 on the right, in the untouched file. That dark patch looks
       * like a retouching artefact and is not one. If this ever needs fixing
       * it is a reshoot or a smooth illumination correction, not a mask.
       *
       * The FILENAMES are in the order the photographs arrived, not the order
       * they run, so this map is deliberately out of sequence: 3, 1, 2, 4, 5.
       * The m10b- files in Shopify Files are the withdrawn darkened set and
       * nothing references them. The five sit across a 1.19x range
       * (0.298 / 0.274 / 0.288 / 0.251 / 0.259 in running order). */
      desk: { 1: 'm10-desk-3.webp',   /* base open, battery and card out */
              2: 'm10-desk-1.webp',   /* front three quarter */
              3: 'm10-desk-2.webp',   /* side profile */
              4: 'm10-desk-4.webp',   /* Arca clamp underneath */
              5: 'm10-desk-5.webp' }, /* on the tripod */
      mob:  { 1: 'm10-mob-3.webp',  2: 'm10-mob-1.webp',  3: 'm10-mob-2.webp',
              4: 'm10-mob-4.webp',  5: 'm10-mob-5.webp' },
      split: 56,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 5, d: 7.2 }
      ]
    },
    {
      id: 'case',
      deskKey: 'SlimCase_Black_Leather_options_ceac1d89',
      mobKey: 'SlimCase_Black_Leather_options_mobile_3cb1ba86',
      /* Added 2026-10-13. This slide stopped being about one product: Indre
       * asked for a COMPARISON of the two cases, each shown with the thing it
       * hides. Red is the FlexCase and holds an AirTag; black is the SlimCase
       * and holds a spare SD card.
       *
       * OPENS ON THE AIRTAG, Indre's order: the hook first, then the red case
       * in the air, then the spare card in the black one, then the black case
       * on the camera to close. The AirTag is the thing nobody expects a
       * leather half case to do, so it earns the opening frame; the two
       * products still read as two chapters, red then black.
       *
       * Four frames, so it closes by returning to frame 1 at 7.2s the way SL3
       * does, and the comparison loops rather than parking on the last one.
       *
       * NOTE FOR WHOEVER TOUCHES THE COPY: the slide's own text still says
       * "The SlimCase, in two finishes ... Silver Chrome or Black Chrome",
       * which was written for a one-product slide and no longer describes
       * these pictures. Flagged to Indre 2026-10-13; the words are a template
       * edit, in BOTH index.json and index.context.us.json.
       *
       * The FILENAMES are in the order the photographs arrived, not the order
       * they run, so this map is deliberately reversed: 4, 3, 2, 1.
       *
       * Cropped only. The four sit across a 1.57x range (0.283 / 0.296 /
       * 0.325 / 0.207 in running order), the widest of any hero - it now ends
       * on the darkest frame rather than starting on it, so the box dims into
       * the loop instead of jumping bright on the first dissolve. Left alone
       * by the rule above; it is the one thing on this slide worth an eye. */
      desk: { 1: 'case-desk-4.webp',   /* FlexCase on the M, AirTag and battery out */
              2: 'case-desk-3.webp',   /* the red case in the air */
              3: 'case-desk-2.webp',   /* the spare SD card in the black case */
              4: 'case-desk-1.webp' }, /* SlimCase on the M, from the base */
      mob:  { 1: 'case-mob-4.webp',  2: 'case-mob-3.webp',
              3: 'case-mob-2.webp',  4: 'case-mob-1.webp' },
      split: 56,
      fade: 0.8,
      steps: [
        { f: 2, d: 1.2 }, { f: 3, d: 3.2 }, { f: 4, d: 5.2 }, { f: 1, d: 7.2 }
      ]
    }
  ];

  var CSS =
    '@keyframes okAnimFade{from{opacity:0}to{opacity:1}}' +
    '.ok-sv{position:absolute;top:0;left:0;height:100%;object-fit:cover;z-index:2;' +
      'background:#000;pointer-events:none}' +
    '.ok-sp{position:absolute;top:0;right:0;height:100%;background:#101012;z-index:2;' +
      'pointer-events:none}' +
    '.ok-pw{position:absolute;top:0;left:0;height:100%;overflow:hidden;z-index:2;' +
      'pointer-events:none}' +
    '.ok-pl{position:absolute;inset:0;background-size:cover;background-position:50% 50%;opacity:0}' +
    '.ok-pl.ok-base{opacity:1}' +
    '.f-slideshow__slide.is-okrun .ok-pl:not(.ok-base){animation-name:okAnimFade;' +
      'animation-timing-function:linear;animation-fill-mode:both}' +
    '@media (prefers-reduced-motion:reduce){.f-slideshow__slide.is-okrun .ok-pl{animation:none}}';

  function injectCss() {
    if (document.getElementById('ok-anim-css')) return;
    var s = document.createElement('style');
    s.id = 'ok-anim-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* Find the slide that owns this item, by the filename of its background. */
  function findSlide(key) {
    if (!key) return null;
    var imgs = document.querySelectorAll('.f-slideshow__slide img');
    for (var i = 0; i < imgs.length; i++) {
      var src = imgs[i].getAttribute('src') || '';
      if (src.indexOf(key) !== -1) {
        var slide = imgs[i].closest ? imgs[i].closest('.f-slideshow__slide') : null;
        if (slide) return slide;
      }
    }
    return null;
  }

  function panelLayer(file, step, fade) {
    var d = document.createElement('div');
    d.className = 'ok-pl';
    d.style.backgroundImage = 'url(' + CDN + file + ')';
    if (!step) { d.className += ' ok-base'; return d; }
    d.style.animationDuration = fade + 's';
    d.style.animationDelay = step.d + 's';
    return d;
  }

  function buildPanel(spec) {
    var slide = findSlide(spec.deskKey) || findSlide(spec.mobKey);
    if (!slide || slide.getAttribute('data-ok-panel')) return null;
    slide.setAttribute('data-ok-panel', spec.id);

    [['.f-slideshow__slide-bg', spec.desk, spec.split],
     ['.f-slideshow__slide-mobile', spec.mob, 100]].forEach(function (pair) {
      var host = slide.querySelector(pair[0]);
      var map = pair[1], width = pair[2];
      if (!host) return;

      var wrap = document.createElement('div');
      wrap.className = 'ok-pw';
      wrap.style.width = width + '%';
      wrap.appendChild(panelLayer(map[1], null, 0));
      spec.steps.forEach(function (step) {
        if (map[step.f]) wrap.appendChild(panelLayer(map[step.f], step, step.fd || spec.fade));
      });
      host.appendChild(wrap);

      /* the right-hand ground for the copy, desktop only: on mobile the theme
       * already puts the text below the picture instead of beside it */
      if (width < 100) {
        var panel = document.createElement('div');
        panel.className = 'ok-sp';
        panel.style.width = (100 - width) + '%';
        host.appendChild(panel);
      }
    });
    return slide;
  }

  function makeVideo(src, widthPct, focus) {
    var v = document.createElement('video');
    v.className = 'ok-sv';
    v.src = src;
    v.muted = true;              /* a hero clip may never make noise unasked */
    v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.preload = 'none';          /* nothing is fetched until we call play() */
    v.style.width = widthPct + '%';
    v.style.objectPosition = focus;
    return v;
  }

  function buildSplit(spec) {
    var slide = findSlide(spec.deskKey) || findSlide(spec.mobKey);
    if (!slide || slide.getAttribute('data-ok-split')) return null;
    slide.setAttribute('data-ok-split', spec.id);

    var out = { slide: slide, els: [] };

    var deskHost = slide.querySelector('.f-slideshow__slide-bg');
    if (deskHost) {
      var dv = makeVideo(spec.desk, spec.split, spec.focus);
      var panel = document.createElement('div');
      panel.className = 'ok-sp';
      panel.style.width = (100 - spec.split) + '%';
      deskHost.appendChild(dv);
      deskHost.appendChild(panel);
      out.els.push(dv);
    }

    var mobHost = slide.querySelector('.f-slideshow__slide-mobile');
    if (mobHost) {
      var mv = makeVideo(spec.mob, 100, spec.focus);
      mobHost.appendChild(mv);
      out.els.push(mv);
    }
    return out.els.length ? out : null;
  }

  function boot() {
    injectCss();

    var slides = [];
    PANELS.forEach(function (spec) {
      var el = buildPanel(spec);
      if (el) slides.push(el);
    });

    var splits = [];
    SPLITS.forEach(function (spec) {
      var s = buildSplit(spec);
      if (s) splits.push(s);
    });

    if (!slides.length && !splits.length) return;

    /* Both guarded, because sync() runs on every class change in the carousel
       and re-adding the class would restart a sequence mid-run, while the
       reflow in reset() is only needed when there is something to rewind. */
    function show(el) {
      if (el.classList.contains('is-okrun')) return;
      el.classList.add('is-okrun');
    }
    function reset(el) {
      if (!el.classList.contains('is-okrun')) return;
      el.classList.remove('is-okrun');
      void el.offsetWidth;   /* forces the animation to start over next time */
    }

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* offsetParent is null when a parent is display:none, which is exactly how
     * the theme hides the breakpoint we are not on. */
    function onScreen(el) { return el.offsetParent !== null; }

    function visibleFraction(el) {
      var r = el.getBoundingClientRect();
      var vh = window.innerHeight || 0;
      if (!r.height) return 0;
      return Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0)) / r.height;
    }

    function carousel(el) {
      return (el.closest && el.closest('.f-slideshow__slides')) || el.parentElement;
    }

    /* "Is this the slide being looked at." Until the carousel has marked one,
       every slide counts, so a theme that stops using is-selected leaves the
       heroes animating rather than frozen on a single frame. */
    function current(el) {
      if (el.classList.contains('is-selected')) return true;
      var box = carousel(el);
      return !(box && box.querySelector('.f-slideshow__slide.is-selected'));
    }

    function live(el) { return visibleFraction(el) >= 0.35 && current(el); }

    function sync() {
      slides.forEach(function (el) { if (live(el)) show(el); else reset(el); });
      if (reduce) return;
      /* Play only the rendition for the breakpoint actually being shown, and
         only while its slide is the one on screen, so the other rendition is
         never downloaded and nothing plays out of sight. */
      splits.forEach(function (s) {
        var on = live(s.slide);
        s.els.forEach(function (v) {
          if (on && onScreen(v)) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            try { v.pause(); } catch (e) {}
          }
        });
      });
    }

    var watched = slides.concat(splits.map(function (s) { return s.slide; }));

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(sync, { threshold: [0, 0.35, 0.7, 1] });
      watched.forEach(function (el) { io.observe(el); });
    }

    /* The carousel advances by swapping the is-selected class, which no
       observer reports and no event in this theme announces, so watch the
       attribute itself. One observer on the container covers every slide. */
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(sync), seen = [];
      watched.forEach(function (el) {
        var box = carousel(el);
        if (!box || seen.indexOf(box) !== -1) return;
        seen.push(box);
        mo.observe(box, { attributes: true, attributeFilter: ['class'], subtree: true });
      });
    }

    var t = null;
    function later() {
      if (t) return;
      t = window.setTimeout(function () { t = null; sync(); }, 120);
    }
    window.addEventListener('scroll', later, { passive: true });
    window.addEventListener('resize', later, { passive: true });

    /* Two passes after first paint: an observer can miss an element that is
     * already on screen while the tab is hidden during load, and flickity sets
     * is-selected some way after this script runs. */
    window.setTimeout(sync, 300);
    window.setTimeout(sync, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
